import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Motion, spring } from 'react-motion';
import Icon from '@loggi/ui/components/icon';
import Label from '@loggi/ui/components/label';
import Waves from '@loggi/ui/components/waves';
import Marker from './marker';
import View from '@loggi/ui/components/view';
import colors from '@loggi/ui/components/utils/colors';
import styles from './styles/map-marker.scss';

const CONTENT_LENGTH = 3;
const INDEX_LABEL_LENGTH = 1;
const WAVES_SIZE = 60;

const STIFFNESS = 100;
const DAMPING = 30;

function tagLength(indexPoint) {
  return indexPoint.length > INDEX_LABEL_LENGTH;
}

function getIcon(icon) {
  return (
    <View inline className={styles.content}>
      <Icon className="pin-icon" type={icon} />
    </View>
  );
}

function MapMarker(props) {
  const wrapClass = classNames([
    styles.wrapPin,
    { [styles.wrapGroup]: props.group },
    props.className
  ]);

  const label = props.label;
  const icon = props.icon ? getIcon(props.icon) : false;
  const content = label || icon || null;
  const color = props.color ? props.color : 'active';

  const pinClasses = classNames([
    { [styles.pin]: !props.group },
    { [styles.pinGroup]: props.group }
  ]);

  const labelPinClasses = classNames([
    [styles.labelPin],
    { [styles.completeHour]: content && content.length > CONTENT_LENGTH },
    { [styles.labelGroup]: props.group },
    { [color]: color }
  ]);

  const wavesCoef = props.group ? WAVES_SIZE * 1.25 : WAVES_SIZE;

  const tag =
    props.indexPoint && !props.group ? (
      <Label
        className={styles.tagPin}
        color={color}
        shape={!tagLength(props.indexPoint) ? 'circle' : null}
        value={props.indexPoint}
        rounded={tagLength(props.indexPoint)}
      />
    ) : null;

  const wave = props.waves ? (
    <Waves className={styles.wavesMarker} color={color} size={wavesCoef} />
  ) : null;

  const pin = props.animationOnMount ? (
    <View style={props.style || {}}>
      <Motion
        defaultStyle={{ scale: 0 }}
        style={{ scale: spring(1, { stiffness: STIFFNESS, damping: DAMPING }) }}
      >
        {style => (
          <View
            className={wrapClass}
            data-color={props.color}
            data-lighten={props.lighten}
            data-loading={props.loading}
            style={{ transform: `scale(${style.scale})` }}
          >
            {wave}
            <Marker
              className={pinClasses}
              tooltip={props.tooltip}
              isGroup={props.group}
            />
            <View className={labelPinClasses}>
              {content}
              {tag}
            </View>
          </View>
        )}
      </Motion>
    </View>
  ) : (
    <View
      style={props.style || {}}
      className={wrapClass}
      data-color={props.color}
      data-lighten={props.lighten}
      data-loading={props.loading}
    >
      {wave}
      <Marker
        className={pinClasses}
        tooltip={props.tooltip}
        isGroup={props.group}
      />
      <View className={labelPinClasses}>
        {content}
        {tag}
      </View>
    </View>
  );
  return pin;
}

MapMarker.displayName = 'MapMarker';

MapMarker.propTypes = {
  animationOnMount: PropTypes.bool,
  delayOnMount: PropTypes.number,
  color: PropTypes.oneOf(colors.typeOptions),
  lighten: PropTypes.bool,
  group: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  indexPoint: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  loading: PropTypes.bool,
  waves: PropTypes.bool
};

MapMarker.defaultProps = {
  animationOnMount: false,
  color: null,
  lighten: false,
  delayOnMount: 1,
  group: false,
  icon: false,
  indexPoint: false,
  label: false,
  loading: null,
  waves: false
};

export default MapMarker;
